import React from 'react';

import {
  GeneratedReportsMetaDocument,
  GeneratedReportsMetaQueryVariables,
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  useApproveVerificationRequestByEmployeeMutation,
  useDeclineVerificationRequestByEmployeeMutation,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useVerificationRequestProcessing } from 'src/hooks/verificationRequest';

import { ApproveOrDeclineVerificationRequestModalContent } from '../ApproveOrDeclineVerificationRequestModalContent';

type ApproveOrDeclineVerificationRequestByEmployeeModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closable: boolean) => void;
  inboxItemId: string;
  action: 'approve' | 'decline';
};

export const ApproveOrDeclineVerificationRequestByEmployeeModalContent: React.FC<
  ApproveOrDeclineVerificationRequestByEmployeeModalContentProps
> = ({ onCloseModal, switchClosability, inboxItemId, action }) => {
  const { createApolloContext } = useNotification();

  const { isVerifierResponding, startVerifierResponding, stopVerifierResponding } =
    useVerificationRequestProcessing();
  const isVerifierResponseInProgress = isVerifierResponding(inboxItemId);

  const refetchQueries = React.useMemo(() => {
    return [
      {
        query: InboxItemDocument,
        variables: { id: inboxItemId } satisfies InboxItemQueryVariables,
      },
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: inboxItemId } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: VerificationRequestDocument,
        variables: { filter: { inboxItemId } } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
      {
        query: GeneratedReportsMetaDocument,
        variables: { inboxItemId } satisfies GeneratedReportsMetaQueryVariables,
      },
    ];
  }, [inboxItemId]);

  const [approveVerificationRequest] = useApproveVerificationRequestByEmployeeMutation({
    refetchQueries,
    context: createApolloContext({
      errorMessage: 'Failed to approve verification request',
      successMessage: 'Verification request approved successfully',
    }),
  });
  const [declineVerificationRequest] = useDeclineVerificationRequestByEmployeeMutation({
    refetchQueries,
    context: createApolloContext({
      errorMessage: 'Failed to decline verification request',
    }),
  });

  const handleAction = React.useCallback(async () => {
    switchClosability(false);
    startVerifierResponding(inboxItemId);

    if (action === 'approve') {
      await approveVerificationRequest({ variables: { inboxItemId } });
    } else {
      await declineVerificationRequest({ variables: { inboxItemId } });
    }

    stopVerifierResponding(inboxItemId);
    switchClosability(true);
    onCloseModal();
  }, [
    action,
    approveVerificationRequest,
    declineVerificationRequest,
    inboxItemId,
    onCloseModal,
    startVerifierResponding,
    stopVerifierResponding,
    switchClosability,
  ]);

  return (
    <ApproveOrDeclineVerificationRequestModalContent
      action={action}
      onCloseModal={onCloseModal}
      handleAction={handleAction}
      disabled={isVerifierResponseInProgress}
      loading={isVerifierResponseInProgress}
    />
  );
};
